import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, Row, Col, Alert } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';

import {
  WelcomeEmptyState,
  LoadingEmptyState,
  NoDataEmptyState,
  NoSelectionEmptyState
} from '../../../components/common/progressemptystate.js';

import Filtros from './components/Filtros';
import ResumenVehiculos from './components/ResumenVehiculos_mejorado';
import TablaDetalle from './components/TablaDetalle';
import CustomProgressBar from './components/ProgressBar';

import { usePaginadorApagadosEncendidos } from './hooks/usePaginadorApagadosEncendidos';
import { procesarApagadosEncendidos, detectarInconsistencias } from './utils/procesadorEventos';

export default function ReporteApagadosEncendidos() {
  const [filtrosLocal, setFiltrosLocal] = useState(null);
  const [datosApagadosEncendidos, setDatosApagadosEncendidos] = useState([]);
  const [resumenVehiculos, setResumenVehiculos] = useState([]);
  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState(null);
  const [vehiculosSeleccionados, setVehiculosSeleccionados] = useState([]); // Nueva state para múltiples vehículos
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [isLoading, setIsLoading] = useState(false);
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [error, setError] = useState(null);
  
  const procesandoRef = useRef(false);
  
  const {
    paginadorApagadosEncendidos,
    cargarPaginasUltraOptimizado,
    resetPaginador
  } = usePaginadorApagadosEncendidos();

  const handleGenerateReport = useCallback(async (filtros) => {
    if (procesandoRef.current) return;
    
    procesandoRef.current = true;
    setIsLoading(true);
    setSearchInitiated(true);
    setFiltrosLocal(filtros);
    setError(null);
    
    try {
      // Reset datos previos
      resetPaginador();
      setDatosApagadosEncendidos([]);
      setResumenVehiculos([]);
      setVehiculoSeleccionado(null);
      setVehiculosSeleccionados([]); // Limpiar selecciones múltiples
      
      // Obtener token info
      const decoded = jwtDecode(localStorage.getItem('token'));
      
      // Configurar parámetros con filtro de tipos específicos
      const parametros = {
        ...filtros,
        tipos: [3, 4, 5, 6], // Solo eventos de apagado (3,5) y encendido (4,6)
        empresaUid: decoded.uid_empresa,
        uid_usuario: decoded.uid_usuario,
        perfil: 1,
        count: true,
        order: 1
      };
      
      console.log('🚀 Generando reporte con parámetros:', parametros);
      
      // Cargar datos
      await cargarPaginasUltraOptimizado(parametros);
      
    } catch (error) {
      console.error('❌ Error generando reporte:', error);
      setError(error.message || 'Error desconocido al generar el reporte');
      setIsLoading(false);
    } finally {
      procesandoRef.current = false;
    }
  }, [cargarPaginasUltraOptimizado, resetPaginador]);

  // Función para manejar selección de vehículos (simple o múltiple)
  const handleVehiculoSelect = useCallback((vehiculosOVehiculo, esMultiple = false) => {
    if (esMultiple) {
      const vehiculos = vehiculosOVehiculo || [];
      
      // 🔧 MEJORA: Si solo hay 1 vehículo seleccionado, tratarlo como selección simple
      if (vehiculos.length === 1) {
        console.log('🔄 Convirtiendo selección múltiple de 1 elemento a selección simple:', vehiculos[0].alias);
        setVehiculoSeleccionado(vehiculos[0]);
        setVehiculosSeleccionados([]); // Limpiar selección múltiple
      } else {
        // Selección múltiple verdadera (0 o más de 1 vehículo)
        setVehiculosSeleccionados(vehiculos);
        setVehiculoSeleccionado(null); // Limpiar selección simple
      }
    } else {
      // Selección simple
      setVehiculoSeleccionado(vehiculosOVehiculo);
      setVehiculosSeleccionados([]); // Limpiar selección múltiple
    }
  }, []);

  // Procesar datos cuando cambien
  useEffect(() => {
    if (paginadorApagadosEncendidos.datos && paginadorApagadosEncendidos.datos.length > 0) {
      console.log('📊 Procesando datos recibidos:', paginadorApagadosEncendidos.datos.length, 'eventos');
      
      // 🔍 ANÁLISIS DETALLADO DE TIPOS DE EVENTOS
      const tiposEventos = {};
      paginadorApagadosEncendidos.datos.forEach(evento => {
        const tipo = evento.tipo;
        if (!tiposEventos[tipo]) {
          tiposEventos[tipo] = { 
            count: 0, 
            ejemplos: [],
            descripcion: evento.evento || 'Sin descripción'
          };
        }
        tiposEventos[tipo].count++;
        
        // Guardar algunos ejemplos para análisis
        if (tiposEventos[tipo].ejemplos.length < 3) {
          tiposEventos[tipo].ejemplos.push({
            fecha: evento.fecha,
            vehiculo: evento.alias || evento.no_economico,
            descripcion: evento.evento
          });
        }
      });
      
      console.log('📋 [ANÁLISIS] Distribución de tipos de eventos:');
      Object.entries(tiposEventos).forEach(([tipo, data]) => {
        console.log(`   Tipo ${tipo}: ${data.count} eventos - "${data.descripcion}"`);
        data.ejemplos.forEach((ejemplo, idx) => {
          console.log(`     📄 Ejemplo ${idx + 1}: ${ejemplo.vehiculo} - ${ejemplo.fecha} - ${ejemplo.descripcion}`);
        });
      });
      
      const datosOrdenados = paginadorApagadosEncendidos.datos.sort((a, b) => 
        new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
      );
      
      const datosProcesados = procesarApagadosEncendidos(datosOrdenados);
      const resumen = detectarInconsistencias(datosProcesados);
      
      console.log('✅ Datos procesados:', {
        vehiculos: datosProcesados.length,
        conInconsistencias: resumen.filter(v => v.tieneInconsistencia).length
      });
      
      setDatosApagadosEncendidos(datosProcesados);
      setResumenVehiculos(resumen);
      setIsLoading(false);
    } else if (searchInitiated && !paginadorApagadosEncendidos.cargando) {
      setIsLoading(false);
    }
  }, [paginadorApagadosEncendidos.datos, paginadorApagadosEncendidos.cargando, searchInitiated]);

  // Efecto para manejar errores del paginador
  useEffect(() => {
    if (paginadorApagadosEncendidos.error) {
      setError(paginadorApagadosEncendidos.error);
      setIsLoading(false);
    }
  }, [paginadorApagadosEncendidos.error]);

  const resumenFiltrado = resumenVehiculos.filter(vehiculo => {
    const diferencia = Math.abs(vehiculo.totalApagados - vehiculo.totalEncendidos);
    
    switch (filtroEstado) {
      case 'criticas':
        return vehiculo.tieneInconsistencia;
      case 'menores':
        return !vehiculo.tieneInconsistencia && diferencia === 1;
      case 'saludables':
        return !vehiculo.tieneInconsistencia && diferencia === 0;
      case 'problemas':
        return vehiculo.tieneInconsistencia || diferencia === 1;
      case 'todos':
      default:
        return true;
    }
  });

  const shouldShowEmptyState = useCallback(() => {
    if (isLoading) return false;
    
    if (!filtrosLocal || !Array.isArray(filtrosLocal.uids_vehiculo) || 
        filtrosLocal.uids_vehiculo.length === 0) {
      return !searchInitiated;
    }
    
    if (searchInitiated && !isLoading && 
        (!datosApagadosEncendidos || datosApagadosEncendidos.length === 0)) {
      return true;
    }
    
    return false;
  }, [isLoading, filtrosLocal, searchInitiated, datosApagadosEncendidos]);

  const getEmptyStateProps = useCallback(() => {
    if (!filtrosLocal || !Array.isArray(filtrosLocal.uids_vehiculo) || 
        filtrosLocal.uids_vehiculo.length === 0) {
      return {
        component: <NoSelectionEmptyState reportType="apagados-encendidos" />,
        show: true
      };
    }

    return {
      component: <NoDataEmptyState reportType="apagados-encendidos" />,
      show: true
    };
  }, [filtrosLocal]);

  // Preparar datos para la tabla según el tipo de selección
  const prepararDatosTabla = useCallback(() => {
    if (vehiculosSeleccionados.length > 0) {
      // Múltiples vehículos: organizar datos por vehículo
      const datosMultiples = {};
      vehiculosSeleccionados.forEach(vehiculo => {
        const datosVehiculo = datosApagadosEncendidos.find(d => d.uid_vehiculo === vehiculo.uid_vehiculo);
        datosMultiples[vehiculo.uid_vehiculo] = datosVehiculo ? datosVehiculo.eventos : [];
      });

      return {
        vehiculo: null,
        datos: null,
        vehiculosMultiples: vehiculosSeleccionados,
        datosMultiples: datosMultiples
      };
    } else if (vehiculoSeleccionado) {
      // Vehículo simple
      return {
        vehiculo: vehiculoSeleccionado,
        datos: datosApagadosEncendidos
          .find(d => d.uid_vehiculo === vehiculoSeleccionado.uid_vehiculo)?.eventos || [],
        vehiculosMultiples: null,
        datosMultiples: null
      };
    }
    return { 
      vehiculo: null, 
      datos: [], 
      vehiculosMultiples: null, 
      datosMultiples: null 
    };
  }, [vehiculosSeleccionados, vehiculoSeleccionado, datosApagadosEncendidos]);

  const datosTabla = prepararDatosTabla();

  return (
    <div className="m-3">
      <Row>
        <Col md={10}>
          <h4 className="mt-2">Apagados y Encendidos</h4>
        </Col>
      </Row>

      <Card className={`p-3 mt-3 ${isLoading ? 'mb-2' : 'mb-3'}`}>
        <hr className="text-danger" />
        
        <Filtros onGenerateReport={handleGenerateReport} />
        
        {error && (
          <Alert variant="danger" className="mt-3">
            <strong>Error:</strong> {error}
          </Alert>
        )}
        
        {isLoading && (
          <div className="mt-2">
            <CustomProgressBar 
              progress={paginadorApagadosEncendidos.progreso || 0}
            />
          </div>
        )}
      </Card>
      
      {/* Renderizado condicional optimizado siguiendo el patrón de alertas */}
      {!searchInitiated ? (
        <WelcomeEmptyState reportType="apagados-encendidos" />
      ) : isLoading ? (
        <LoadingEmptyState reportType="apagados-encendidos" />
      ) : shouldShowEmptyState() ? (
        getEmptyStateProps().component
      ) : (
        <>
          {resumenVehiculos.length > 0 && (
            <>
              <ResumenVehiculos 
                resumen={resumenFiltrado}
                onVehiculoSelect={handleVehiculoSelect}
                vehiculoSeleccionado={vehiculoSeleccionado}
                vehiculosSeleccionados={vehiculosSeleccionados}
                filtroEstado={filtroEstado}
                onFiltroEstadoChange={setFiltroEstado}
              />
              
              <TablaDetalle 
                vehiculo={datosTabla.vehiculo}
                datos={datosTabla.datos}
                vehiculosMultiples={datosTabla.vehiculosMultiples}
                datosMultiples={datosTabla.datosMultiples}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
